import { ApiError, api } from '../../shared/http/ApiClient.js';
import { BaseViewModel } from '../../shared/viewmodels/BaseViewModel.js';
import { NotaRepository } from '../models/repositories/NotaRepository.js';
import type { NotaData } from '../models/entities/NotaData.js';

interface State {
  loading: boolean;
  data: NotaData | null;
  error: string | null;
  /** 404 en navegación client-side: la vista muestra la página de no encontrada. */
  noEncontrada: boolean;
  hydrated: boolean;
}

export interface DireccionDeNota {
  year: string;
  month: string;
  urlKey: string;
}

function clave(d: DireccionDeNota): string {
  return `${d.year}/${d.month}/${d.urlKey}`;
}

/** ViewModel de la nota. Hidratado del SSR; `load()` real solo en navegación client-side. */
export class NotaViewModel extends BaseViewModel<State> {
  private readonly repo = new NotaRepository({ get: (p) => api.get(p) });
  /** La última dirección pedida. Lo que vuelva de una anterior se descarta. */
  private ultimaPedida: string;

  constructor(
    initial: NotaData | null,
    private direccion: DireccionDeNota,
  ) {
    super({
      loading: false,
      data: initial,
      error: null,
      noEncontrada: false,
      hydrated: initial !== null,
    });
    this.ultimaPedida = clave(direccion);
  }

  /** Primera carga: no-op si la nota vino del server. */
  async load(): Promise<void> {
    await this.irA(this.direccion);
  }

  /**
   * Muestra otra nota **sin remontar la pantalla**.
   *
   * De una nota a otra la ruta es la misma (`/:year/:month/:urlKey`), así que React Router
   * mantiene la vista montada y solo cambian los params: sin esto, el VM se quedaba con la
   * nota anterior y la URL cambiaba sin que cambiara la pantalla.
   */
  async irA(direccion: DireccionDeNota): Promise<void> {
    const destino = clave(direccion);
    const actual = this.state$.value;
    // La misma nota ya resuelta (hidratada del SSR o traída recién): no se vuelve a pedir.
    if (destino === clave(this.direccion) && actual.hydrated) return;

    this.direccion = direccion;
    this.ultimaPedida = destino;
    // `data: null` es lo que hace que la vista muestre el esqueleto: dejar la nota anterior
    // a la vista mientras carga la nueva confunde, porque el título ya no corresponde.
    this.patch({ loading: true, data: null, error: null, noEncontrada: false, hydrated: false });

    try {
      const { year, month, urlKey } = direccion;
      const data = await this.repo.porDireccion(year, month, urlKey);
      // Clicks rápidos entre notas dejan varios pedidos en vuelo y pueden volver
      // desordenados: el que ya no es el destino actual no pinta nada.
      if (this.ultimaPedida !== destino) return;
      this.patch({ data, loading: false, hydrated: true });
    } catch (err) {
      if (this.ultimaPedida !== destino) return;
      if (err instanceof ApiError && err.status === 404) {
        this.patch({ loading: false, noEncontrada: true });
        return;
      }
      this.patch({ loading: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }
}
